import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";

export interface CandidatesUrlState {
    entityId: string | undefined;
    page: number;
    pageSize: number;
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    candidateId: string | undefined;
    candidateEntityId: string | undefined;
}

export interface CandidatesUrlStateActions {
    setEntityId: (entityId: string | undefined) => void;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    setSorting: (sorting: SortingState) => void;
    setColumnFilters: (filters: ColumnFiltersState) => void;
    /** Sets column filters and sorting together in a single URL update (avoids clobbering when both need to change at once, e.g. applying a preset). */
    applyFilterState: (filters: ColumnFiltersState, sorting: SortingState) => void;
    setCandidateId: (candidateId: string | undefined, candidateEntityId?: string) => void;
    clearFilters: () => void;
}

const DEFAULT_PAGE_SIZE = 50;

function encodeFilters(filters: ColumnFiltersState): string {
    if (filters.length === 0) return "";
    try {
        return btoa(JSON.stringify(filters));
    } catch {
        return "";
    }
}

function decodeFilters(encoded: string): ColumnFiltersState {
    if (!encoded) return [];
    try {
        return JSON.parse(atob(encoded));
    } catch {
        return [];
    }
}

function encodeSorting(sorting: SortingState): string {
    return sorting.map(s => `${s.id}:${s.desc ? "desc" : "asc"}`).join(",");
}

function decodeSorting(encoded: string): SortingState {
    if (!encoded) return [];
    return encoded.split(",").flatMap(part => {
        const [id, dir] = part.split(":");
        if (!id) return [];
        return [{ id, desc: dir === "desc" }];
    });
}

export function useCandidatesUrlState(): CandidatesUrlState & CandidatesUrlStateActions {
    const [searchParams, setSearchParams] = useSearchParams();

    const entityId = searchParams.get("eId") ?? undefined;
    const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
    const pageSize = Math.min(200, Math.max(1, parseInt(searchParams.get("pageSize") ?? String(DEFAULT_PAGE_SIZE), 10) || DEFAULT_PAGE_SIZE));
    const sorting = decodeSorting(searchParams.get("sort") ?? "");
    const columnFilters = decodeFilters(searchParams.get("filters") ?? "");
    const candidateId = searchParams.get("candidateId") ?? undefined;
    const candidateEntityId = searchParams.get("ceId") ?? undefined;

    const update = useCallback(
        (updater: (prev: URLSearchParams) => void) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    updater(next);
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const setEntityId = useCallback(
        (id: string | undefined) => {
            update(p => {
                if (id) p.set("eId", id);
                else p.delete("eId");
                p.set("page", "1"); // reset to first page on entity change
            });
        },
        [update]
    );

    const setPage = useCallback((p: number) => update(params => params.set("page", String(p))), [update]);

    const setPageSize = useCallback(
        (size: number) =>
            update(p => {
                p.set("pageSize", String(size));
                p.set("page", "1");
            }),
        [update]
    );

    const setSorting = useCallback(
        (s: SortingState) =>
            update(p => {
                p.set("page", "1");
                if (s.length > 0) p.set("sort", encodeSorting(s));
                else p.delete("sort");
            }),
        [update]
    );

    const setColumnFilters = useCallback(
        (filters: ColumnFiltersState) =>
            update(p => {
                p.set("page", "1");
                if (filters.length > 0) p.set("filters", encodeFilters(filters));
                else p.delete("filters");
            }),
        [update]
    );

    // Sets filters and sorting in one atomic URL update. Calling setColumnFilters() and setSorting()
    // separately would clobber each other: react-router's useSearchParams setter closes over the
    // search params snapshot from the current render, so two calls in the same tick both build off
    // the same stale snapshot and the second call's navigation overwrites the first's change.
    const applyFilterState = useCallback(
        (filters: ColumnFiltersState, s: SortingState) =>
            update(p => {
                p.set("page", "1");
                if (filters.length > 0) p.set("filters", encodeFilters(filters));
                else p.delete("filters");
                if (s.length > 0) p.set("sort", encodeSorting(s));
                else p.delete("sort");
            }),
        [update]
    );

    const setCandidateId = useCallback(
        (id: string | undefined, entityIdForCandidate?: string) => {
            update(p => {
                if (id) {
                    p.set("candidateId", id);
                    if (entityIdForCandidate) p.set("ceId", entityIdForCandidate);
                } else {
                    p.delete("candidateId");
                    p.delete("ceId");
                }
            });
        },
        [update]
    );

    const clearFilters = useCallback(
        () =>
            update(p => {
                p.delete("filters");
                p.delete("sort");
                p.set("page", "1");
            }),
        [update]
    );

    return {
        entityId,
        page,
        pageSize,
        sorting,
        columnFilters,
        candidateId,
        candidateEntityId,
        setEntityId,
        setPage,
        setPageSize,
        setSorting,
        setColumnFilters,
        applyFilterState,
        setCandidateId,
        clearFilters,
    };
}
